/** biome-ignore-all lint/suspicious/noExplicitAny: it's ok */
import type { JSONContent } from "@tiptap/core";
import assert from "assert";
import type { ComponentType, ReactNode } from "react";
import React from "react";
import type { RenderableTreeNodes, Scalar } from "../logic/Markdoc";

type Component = ComponentType<unknown>;

export default function Renderer(
	mainNode: RenderableTreeNodes | JSONContent,
	{ components = {} } = {},
	isPrint?: boolean,
	onRender?: VoidFunction,
) {
	function deepRender(value: any): any {
		if (value == null || typeof value !== "object") return value;
		if (Array.isArray(value)) return value.map(deepRender);

		if (value.$$mdtype === "Tag") return render(value);

		if (typeof value !== "object") return value;

		const output: Record<string, Scalar> = {};
		for (const [k, v] of Object.entries(value)) output[k] = deepRender(v);
		return output;
	}

	function render(node: RenderableTreeNodes | JSONContent): ReactNode {
		if (Array.isArray(node)) return React.createElement(React.Fragment, null, ...node.map(render));
		if (typeof node === "string") return node;
		if (node === null) return null;

		const { name, attributes: { class: className, ...attrs } = {}, children = [] } = getNodeData(node);

		if (className) attrs.className = className;

		let renderAttrs = Object.keys(attrs).length === 0 ? null : deepRender(attrs);
		if (renderAttrs && isPrint) renderAttrs.isPrint = true;
		if (isPrint && !renderAttrs) renderAttrs = { isPrint };
		return React.createElement(tagName(name, components), renderAttrs, ...children.map(render));
	}

	if (!mainNode) return null;
	const component = render(mainNode);
	onRender?.();
	return component;
}

function getNodeData(node: RenderableTreeNodes | JSONContent) {
	assert(typeof node === "object", "node must be an object and not null");

	const isTag = "children" in node;
	if (isTag) return node;

	if ("type" in node) {
		return {
			name: node.type,
			attributes: node.attrs,
			children: node.content,
		};
	}

	return {};
}

function tagName(
	name: string,
	components: Record<string, Component> | ((string: string) => Component),
): string | Component {
	return typeof name !== "string" ? "div" : components[name] ? components[name] : name;
}
